import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import WarningIcon from '../ui/WarningIcon';
import CatalogAssignmentIndicator from '../ui/CatalogAssignmentIndicator';
import TerminalGroupChip from '../ui/TerminalGroupChip';
import CableColorRect from '../ui/CableColorRect';
import { Icons } from '../icons/Icons';
import { useI18n } from '../../context/I18nContext';
import { formatSezione, getCableWidthForTessera, getMultipolarPoleConnections, getTerminalGroupForEndpoint } from '../../utils/cableLogic';
import { normalizeColorToCanonical, translateColorForDisplay } from '../../utils/colorUtils';
import MachineConfigPanel from './MachineConfigPanel';
import MultipleGroupBox from './MultipleGroupBox';
import { getCableCatalogStatus } from '../../utils/cableCatalog';

const MultipolarTessera = ({
  cable,
  clickable,
  onCableClick,
  onEditMultipolarCable,
  onDeleteMultipolarCable,
  settings,
  validateCable,
  isDragEnabled,
  isDragging,
  isDragOver,
  hasIssues,
  isHovered,
  editingIndex,
  editingMultipolarIndex,
  showDeleteButton,
  onToggleDelete,
  isSelectionModeEnabled,
  selectedCables,
  onToggleSelection,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  draggedIndex,
  dragOverIndex,
  relatedCables,
  terminalGroups = null,
  renderConnectedCable
}) => {
  const [showMachineConfig, setShowMachineConfig] = useState(false);
  const [poleWireBounds, setPoleWireBounds] = useState({});
  const poleContainerRefs = useRef({});
  const poleSideRefs = useRef({});
  const { t } = useI18n();

  const poleConnections = useMemo(
    () => getMultipolarPoleConnections(cable, relatedCables, settings),
    [cable, relatedCables, settings]
  );
  const validation = useMemo(() => validateCable(cable), [cable, validateCable]);
  const actualHasIssues = hasIssues || !validation.isValid;
  const catalogStatus = getCableCatalogStatus(cable);
  const isBeingEdited = editingMultipolarIndex === cable.originalIndex;
  const shouldShowDelete = Boolean(showDeleteButton || isBeingEdited);

  const containerClasses = useMemo(() => {
    const baseClasses = ['border-2', 'rounded-lg', 'transition-all', 'duration-200'];

    if (isDragEnabled) baseClasses.push('cursor-move');
    else if (clickable) baseClasses.push('cursor-pointer');

    if (actualHasIssues) {
      baseClasses.push('bg-amber-50', 'border-amber-300', 'hover:bg-amber-100', 'hover:border-amber-400');
    } else if (isHovered) {
      baseClasses.push('bg-blue-50', 'border-blue-300', '-translate-y-px', 'shadow-[0_4px_8px_rgba(59,130,246,0.3)]');
    } else {
      baseClasses.push('bg-gradient-to-r', 'from-amber-50', 'to-yellow-50', 'border-amber-200', 'hover:border-amber-300');
      if (isBeingEdited) baseClasses.push('border-amber-300 shadow-sm');
    }

    if (isDragEnabled && !actualHasIssues) {
      baseClasses.push('hover:bg-amber-50', 'hover:border-amber-300');
    }

    if (isDragging) {
      baseClasses.push('opacity-50', 'transform', 'scale-95', 'border-blue-400');
    }

    if (isDragOver) {
      baseClasses.push('border-blue-500', 'bg-blue-100', '-translate-y-px', 'shadow-[0_4px_8px_rgba(59,130,246,0.3)]');
    }

    if (actualHasIssues && !isDragging && !isDragOver && !isHovered) {
      baseClasses.push('shadow-[0_2px_4px_rgba(245,158,11,0.2)]');
    }

    return baseClasses.join(' ');
  }, [isDragEnabled, clickable, actualHasIssues, isHovered, isBeingEdited, isDragging, isDragOver]);

  const setPoleContainerRef = useCallback((poloIndex, node) => {
    if (node) {
      poleContainerRefs.current[poloIndex] = node;
    } else {
      delete poleContainerRefs.current[poloIndex];
    }
  }, []);

  const setPoleSideRef = useCallback((poloIndex, side, node) => {
    const key = `${poloIndex}-${side}`;
    if (node) {
      poleSideRefs.current[key] = node;
    } else {
      delete poleSideRefs.current[key];
    }
  }, []);

  const measurePoleWires = useCallback(() => {
    const nextBounds = {};

    cable.poli.forEach((_, poloIndex) => {
      const container = poleContainerRefs.current[poloIndex];
      const sideA = poleSideRefs.current[`${poloIndex}-A`];
      const sideB = poleSideRefs.current[`${poloIndex}-B`];

      if (!container || !sideA || !sideB) return;

      const containerRect = container.getBoundingClientRect();
      const sideARect = sideA.getBoundingClientRect();
      const sideBRect = sideB.getBoundingClientRect();
      const start = sideARect.top + sideARect.height / 2 - containerRect.top;
      const end = sideBRect.top + sideBRect.height / 2 - containerRect.top;
      const top = Math.round(Math.min(start, end));
      const height = Math.max(8, Math.round(Math.abs(end - start)));

      nextBounds[poloIndex] = { top, height };
    });

    setPoleWireBounds(prevBounds => {
      const prevKeys = Object.keys(prevBounds);
      const nextKeys = Object.keys(nextBounds);
      const hasSameBounds = prevKeys.length === nextKeys.length &&
        nextKeys.every(key =>
          prevBounds[key]?.top === nextBounds[key].top &&
          prevBounds[key]?.height === nextBounds[key].height
        );

      return hasSameBounds ? prevBounds : nextBounds;
    });
  }, [cable.poli]);

  useLayoutEffect(() => {
    measurePoleWires();
    window.addEventListener('resize', measurePoleWires);
    return () => window.removeEventListener('resize', measurePoleWires);
  }, [measurePoleWires, poleConnections, isDragEnabled, draggedIndex, dragOverIndex, showDeleteButton]);

  const handleClick = useCallback(() => {
    if (clickable && onCableClick) {
      const title = cable.nomeCavo || `${t('schematic.multipolarCable')} ${cable.poli.length}P`;
      onCableClick(title, cable.originalIndex);
    }
  }, [clickable, onCableClick, cable, t]);

  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    if (onToggleDelete) onToggleDelete();
    onEditMultipolarCable?.(cable.originalIndex);
  }, [onToggleDelete, onEditMultipolarCable, cable.originalIndex]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    onDeleteMultipolarCable(cable.originalIndex);
  }, [onDeleteMultipolarCable, cable.originalIndex]);

  // Il chip segnala che questo polo finisce in un morsetto condiviso con altri poli o cavi.
  const renderTerminalGroupChip = (poloIndex, side) => {
    const match = getTerminalGroupForEndpoint(terminalGroups, cable, side, poloIndex);
    if (!match) return null;

    return (
      <TerminalGroupChip
        groupIndex={match.groupIndex}
        group={match.group}
        lingua={settings?.lingua}
        useAWG={settings?.useAWG}
      />
    );
  };

  const renderPoleEndpointRow = (polo, poloIndex, side, attachMeasureRef = false) => {
    const isSideA = side === 'A';
    const siglatura = isSideA ? polo.siglaturaA : polo.siglaturaB;
    const terminale = isSideA ? polo.terminaleA : polo.terminaleB;
    const collegamento = isSideA ? polo.collegamentoA : polo.collegamentoB;
    const lunghezza = isSideA ? polo.lunghezzaA : polo.lunghezzaB;
    const isEarthWire = normalizeColorToCanonical(polo.colore) === 'YE/GN';
    const displaySiglatura = siglatura || (isEarthWire ? t('cableLabels.earthWire') : `${cable.nomeCavo || t('schematic.multipolarCable')} P${poloIndex + 1}${side}`);

    return (
      <div
        key={`pole-endpoint-${side}-${cable.originalIndex}-${poloIndex}`}
        ref={attachMeasureRef ? (node) => setPoleSideRef(poloIndex, side, node) : null}
        className="flex items-center gap-2 text-sm bg-white border border-green-100 rounded px-2 py-1"
      >
        <span className={`px-2 py-0.5 rounded text-xs font-bold min-w-[24px] text-center ${isSideA ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {side}
        </span>
        <span className="font-medium text-gray-800">{displaySiglatura}</span>
        <span className="text-gray-500">{translateColorForDisplay(polo.colore, settings.lingua)}</span>
        <span className="text-gray-400">•</span>
        <span className="text-gray-500">{lunghezza || 0}mm</span>
        <span className="text-gray-400">•</span>
        {renderTerminalGroupChip(poloIndex, side)}
        <span className="font-medium text-gray-600">{terminale}</span>
        <span className="text-gray-400">|</span>
        <span className="font-medium text-gray-600">{collegamento || '—'}</span>
      </div>
    );
  };

  const renderNestedPoleConnections = (polo, poloIndex, connections, side) => {
    if (!connections || connections.length === 0) return null;
    const hasNestedIssues = connections.some(connectedCable => !validateCable(connectedCable).isValid);

    return (
      <MultipleGroupBox
        title={`${t('cableList.multiple')} ${side}`}
        count={connections.length + 1}
        hasIssues={hasNestedIssues}
        compact
      >
        {renderPoleEndpointRow(polo, poloIndex, side, true)}
        {connections.map((connectedCable) => {
          const isGlobalDragging = draggedIndex === connectedCable.originalIndex;
          const isGlobalDragOver = dragOverIndex === connectedCable.originalIndex && draggedIndex !== connectedCable.originalIndex;

          return (
            <div
              key={`pole-link-${side}-${connectedCable.originalIndex}`}
              className="relative"
              draggable={isDragEnabled}
              onDragStart={(e) => {
                e.stopPropagation();
                handleDragStart?.(e, connectedCable.originalIndex);
              }}
              onDragEnd={(e) => {
                e.stopPropagation();
                handleDragEnd?.(e);
              }}
              onDragOver={(e) => {
                e.stopPropagation();
                handleDragOver?.(e, connectedCable.originalIndex);
              }}
              onDragEnter={(e) => {
                e.stopPropagation();
                handleDragEnter?.(e, connectedCable.originalIndex);
              }}
              onDragLeave={(e) => {
                e.stopPropagation();
                handleDragLeave?.(e);
              }}
              onDrop={(e) => {
                e.stopPropagation();
                handleDrop?.(e, connectedCable.originalIndex);
              }}
            >
              {isDragEnabled && (
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-green-600 transition-colors z-10">
                  <Icons.DragHandle className="w-4 h-4" />
                </div>
              )}
              {renderConnectedCable(connectedCable, {
                isDragging: isGlobalDragging,
                isDragOver: isGlobalDragOver,
                relatedCables: connections
              })}
            </div>
          );
        })}
      </MultipleGroupBox>
    );
  };

  const renderNormalPoleSide = (polo, poloIndex, side, connections) => {
    const isSideA = side === 'A';
    if (connections && connections.length > 0) {
      return renderNestedPoleConnections(polo, poloIndex, connections, side);
    }

    const siglatura = isSideA ? polo.siglaturaA : polo.siglaturaB;
    const terminale = isSideA ? polo.terminaleA : polo.terminaleB;
    const collegamento = isSideA ? polo.collegamentoA : polo.collegamentoB;
    const lunghezza = isSideA ? polo.lunghezzaA : polo.lunghezzaB;

    return (
      <div
        ref={(node) => setPoleSideRef(poloIndex, side, node)}
        className={`flex items-center gap-2 ${isSideA ? 'mb-2' : ''}`}
      >
        <div className={`${isSideA ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} px-2 py-1 rounded text-xs font-bold min-w-[24px] text-center`}>
          {side}
        </div>
        {!settings.marcaturePerCollegamento && (
          <span className="text-sm font-medium">
            {(() => {
              const isEarthWire = normalizeColorToCanonical(polo.colore) === 'YE/GN';
              if (isEarthWire) return t('cableLabels.earthWire');
              return siglatura || '';
            })()}
          </span>
        )}
        <span className="text-sm text-gray-500 flex-shrink-0">{translateColorForDisplay(polo.colore, settings.lingua)}</span>
        <span className="text-gray-400">•</span>
        {(() => {
          const sezioni = [...new Set(cable.poli.map(p => p.sezione))];
          const hasUniformSection = sezioni.length === 1;

          return !hasUniformSection ? (
            <>
              <span className="text-sm text-gray-500 flex-shrink-0">
                {formatSezione(polo.sezione, settings.useAWG)}
              </span>
              <span className="text-gray-400">•</span>
            </>
          ) : null;
        })()}
        <span className="text-sm text-gray-500">{lunghezza || 0}mm</span>
        <span className="text-gray-400">•</span>
        {renderTerminalGroupChip(poloIndex, side)}
        <span className="text-sm font-medium text-gray-600">{terminale}</span>
        <span className="text-gray-400">|</span>
        <span className="text-sm font-medium text-gray-600">{collegamento || '—'}</span>
      </div>
    );
  };

  return (
    <div className={`${containerClasses} p-4 relative group`} onClick={handleClick}>
      <div className={`flex items-center justify-between mb-4 ${isDragEnabled ? 'ml-8' : ''}`}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isSelectionModeEnabled && (
            <label
              className="flex items-center cursor-pointer flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelection?.(cable.originalIndex);
              }}
            >
              <input
                type="checkbox"
                checked={selectedCables.has(cable.originalIndex)}
                onChange={() => onToggleSelection?.(cable.originalIndex)}
                onClick={(e) => e.stopPropagation()}
                className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
              />
            </label>
          )}

          {!isDragEnabled && actualHasIssues && (
            <div className="flex-shrink-0">
              <WarningIcon
                issues={validation.issues}
                lingua={settings?.lingua}
                className="flex-shrink-0"
              />
            </div>
          )}

          <span className="relative flex-shrink-0">
            <span className={`block px-2 py-0.5 rounded text-sm font-medium ${actualHasIssues ? 'bg-amber-200 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
              #{cable.originalIndex + 1}
            </span>
            <CatalogAssignmentIndicator
              code={cable.catalogAssignment?.codiceInterno}
              description={cable._catalogDescription}
              status={catalogStatus}
            />
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {cable.nomeCavo || t('cable.multipolarCable')}
              </span>
              <span className="text-sm text-gray-500 flex-shrink-0">
                {(() => {
                  const sezioni = [...new Set(cable.poli.map(p => p.sezione))];
                  const hasUniformSection = sezioni.length === 1;

                  return hasUniformSection
                    ? `${formatSezione(sezioni[0], settings.useAWG)} • ${cable.lunghezza}mm`
                    : `${cable.lunghezza}mm`;
                })()}
              </span>
            </div>
          </div>
        </div>

        <div className="relative flex items-center justify-end flex-shrink-0 min-h-[32px]">
          <div className={`bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium transition-opacity duration-200 ${isBeingEdited ? 'opacity-0' : 'group-hover:opacity-0'}`}>
            {t('schematic.multipolarCable')}
          </div>

          <div className={`absolute right-0 items-center gap-1 transition-opacity duration-200 ${isBeingEdited ? 'flex opacity-100' : 'flex opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto'}`}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMachineConfig(true);
              }}
              className="p-2 rounded transition-colors text-indigo-600 hover:bg-indigo-100"
              title="Configurazione macchina spelafili"
            >
              <Icons.Settings className="w-4 h-4" />
            </button>

            {!shouldShowDelete ? (
              <button
                onClick={handleEdit}
                disabled={isDragEnabled || isSelectionModeEnabled || ((editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited)}
                className={`p-2 rounded transition-colors ${isDragEnabled || isSelectionModeEnabled || ((editingIndex >= 0 || editingMultipolarIndex >= 0) && !isBeingEdited)
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-amber-600 hover:bg-amber-100'
                  }`}
                title="Modifica cavo multipolare"
              >
                <Icons.Edit3 className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleDelete}
                disabled={isDragEnabled}
                className={`p-2 rounded transition-colors ${isDragEnabled ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
                title="Cancella cavo multipolare definitivamente"
              >
                <Icons.Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {cable.poli.map((polo, index) => {
          const poloWidth = getCableWidthForTessera(polo.sezione, settings.useAWG);
          const nestedConnections = poleConnections[index] || {};

          return (
            <div
              key={polo.id || index}
              className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-xs font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded flex-shrink-0 min-w-[28px] text-center">
                    P{index + 1}
                  </span>

                  <div
                    ref={(node) => setPoleContainerRef(index, node)}
                    className="relative flex-shrink-0 self-stretch"
                    style={{ width: poloWidth, minHeight: '45px' }}
                  >
                    <div
                      className="absolute left-0 right-0 transition-all duration-200"
                      style={poleWireBounds[index]
                        ? {
                          top: `${poleWireBounds[index].top}px`,
                          height: `${poleWireBounds[index].height}px`
                        }
                        : {
                          top: '50%',
                          height: '45px',
                          transform: 'translateY(-50%)'
                        }}
                    >
                      <CableColorRect
                        color={polo.colore}
                        width="100%"
                        height="100%"
                        borderRadius="9999px"
                        className="transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div>
                      {renderNormalPoleSide(polo, index, 'A', nestedConnections.connectionsA)}
                    </div>
                    <div className="border-t border-gray-100 my-2"></div>
                    <div>
                      {renderNormalPoleSide(polo, index, 'B', nestedConnections.connectionsB)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showMachineConfig && (
        <MachineConfigPanel
          cable={cable}
          onClose={() => setShowMachineConfig(false)}
          settings={settings}
        />
      )}
    </div>
  );
};

export default MultipolarTessera;
